import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import Orientation, { OrientationType } from 'react-native-orientation-locker';
import { useAnimatedStyle, withTiming, useSharedValue, interpolate, Extrapolation, } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PlaybackEvent, PlaybackEventEmitter, } from '@annunciate/core';
import { VALID_FROM_ROTATIONS, VALID_TO_ROTATIONS, } from '../../../constants/Rotations';
import { useTheme } from '../../useTheme';
import { useIsPlayerMinimized } from '../useIsPlayerMinimized';
import { playerRef, isLandscape, isPortrait, isPortraitUpsidedown, } from '../../../utils/player';
import { useValidOrientations } from '../../utils/useValidOrientations';
import { StatusBarMode, useStatusBarModeStore } from '../../../components';
import { useWindowDimensions } from 'react-native';
export const useAnimatedVideoStyle = ({ position, animationTiming = 250, }) => {
    const { miniplayerHeight } = useTheme();
    const { appOrientation, deviceOrientation } = useValidOrientations();
    const { top: topInset, bottom: bottomInset, } = useSafeAreaInsets();
    const isRotationPaused = useSharedValue(false);
    const isMinimized = useIsPlayerMinimized();
    const setMode = useStatusBarModeStore((state) => state.setMode);
    const lastRotationRef = useRef(undefined);
    const lastDesiredPlayerOrientationRef = useRef(undefined);
    const derivedRotation = useSharedValue(0);
    const [desiredPlayerOrientation, setDesiredPlayerOrientation,] = useState(OrientationType['PORTRAIT']);
    useEffect(() => {
        if (!deviceOrientation)
            return;
        onCalculateRotationAndDesiredPlayerOrientation({
            appOrientation,
            deviceOrientation,
        });
    }, [
        deviceOrientation,
        appOrientation,
    ]);
    const onCalculateRotationAndDesiredPlayerOrientation = useCallback(({ deviceOrientation, appOrientation = OrientationType['PORTRAIT'], forcedState, }) => {
        setDesiredPlayerOrientation((oldOrientation) => {
            let rotation = 0;
            let newDesiredPlayerOrientation = oldOrientation;
            if (forcedState) {
                if (forcedState === 'not-fullscreen' &&
                    Orientation.isLocked()) {
                    rotation = 0;
                    newDesiredPlayerOrientation = OrientationType['PORTRAIT'];
                }
                else if (forcedState === 'not-fullscreen' &&
                    !Orientation.isLocked()) {
                    rotation = 0;
                    newDesiredPlayerOrientation = OrientationType['PORTRAIT'];
                }
                else if (forcedState === 'fullscreen' &&
                    Orientation.isLocked()) {
                    newDesiredPlayerOrientation = isLandscape(deviceOrientation)
                        ? deviceOrientation
                        : OrientationType['LANDSCAPE-LEFT'];
                    rotation = newDesiredPlayerOrientation === OrientationType['LANDSCAPE-LEFT']
                        ? 0.25
                        : -0.25;
                }
                else if (forcedState === 'fullscreen' &&
                    !Orientation.isLocked()) {
                    rotation = 0.25;
                    newDesiredPlayerOrientation = OrientationType['LANDSCAPE-LEFT'];
                }
            }
            else {
                if (isLandscape(deviceOrientation)
                    && Orientation.isLocked()) {
                    rotation = deviceOrientation === OrientationType['LANDSCAPE-LEFT']
                        ? 0.25
                        : -0.25;
                    newDesiredPlayerOrientation = deviceOrientation;
                }
                else if (isLandscape(deviceOrientation)
                    && !Orientation.isLocked()) {
                    rotation = 0;
                    newDesiredPlayerOrientation = OrientationType['PORTRAIT'];
                }
                else if (isPortraitUpsidedown(deviceOrientation)
                    && Orientation.isLocked()) {
                    rotation = lastRotationRef.current === undefined
                        ? 0
                        : lastRotationRef.current;
                    newDesiredPlayerOrientation =
                        lastDesiredPlayerOrientationRef.current === undefined
                            ? OrientationType['PORTRAIT']
                            : lastDesiredPlayerOrientationRef.current;
                }
                else if (isPortrait(deviceOrientation)) {
                    rotation = 0;
                    newDesiredPlayerOrientation = OrientationType['PORTRAIT'];
                }
            }
            const validToFromRotations = newDesiredPlayerOrientation
                && VALID_FROM_ROTATIONS.includes(appOrientation)
                && VALID_TO_ROTATIONS.includes(newDesiredPlayerOrientation);
            if (validToFromRotations) {
                requestAnimationFrame(() => {
                    derivedRotation.value = withTiming(rotation, { duration: animationTiming });
                });
            }
            lastRotationRef.current = rotation;
            lastDesiredPlayerOrientationRef.current = newDesiredPlayerOrientation;
            return newDesiredPlayerOrientation;
        });
    }, [
        deviceOrientation,
        appOrientation,
        desiredPlayerOrientation,
        isMinimized,
    ]);
    const { width: screenWidth, height: screenHeight, } = useWindowDimensions();
    const longVal = Math.max(screenWidth, screenHeight);
    const shortVal = Math.min(screenWidth, screenHeight);
    const isAppPortrait = isPortrait(appOrientation);
    const isAppLandscape = isLandscape(appOrientation);
    const isDevicePortraitOrUpsidedown = isPortrait(deviceOrientation)
        || isPortraitUpsidedown(deviceOrientation);
    const isDeviceLandscape = isLandscape(deviceOrientation);
    const isDesiredPlayerOrientationPortrait = isPortrait(desiredPlayerOrientation);
    const isDesiredPlayerOrientationLandscape = isLandscape(desiredPlayerOrientation);
    const isPlayerEmbedded = isAppPortrait
        && isDesiredPlayerOrientationPortrait;
    const isFullscreen = !isPlayerEmbedded && !isMinimized;
    const onToggleFullscreen = useCallback(() => {
        const shouldExitFullscreen = desiredPlayerOrientation !== appOrientation
            || (isLandscape(appOrientation) && isLandscape(desiredPlayerOrientation));
        if (shouldExitFullscreen && isLandscape(appOrientation)) {
            playerRef.current?.collapse();
        }
        else if (shouldExitFullscreen) {
            onCalculateRotationAndDesiredPlayerOrientation({
                appOrientation,
                deviceOrientation,
                forcedState: 'not-fullscreen',
            });
        }
        else {
            onCalculateRotationAndDesiredPlayerOrientation({
                appOrientation,
                deviceOrientation,
                forcedState: 'fullscreen',
            });
        }
    }, [
        desiredPlayerOrientation,
        deviceOrientation,
        appOrientation,
        playerRef,
    ]);
    const onBack = useCallback(() => {
        if (isLandscape(appOrientation)
            || (isPortrait(appOrientation) && isPortrait(desiredPlayerOrientation))) {
            playerRef.current?.collapse();
        }
        else {
            onCalculateRotationAndDesiredPlayerOrientation({
                appOrientation,
                deviceOrientation,
                forcedState: 'not-fullscreen',
            });
        }
    }, [
        appOrientation,
        deviceOrientation,
        desiredPlayerOrientation,
        playerRef,
    ]);
    const style = useAnimatedStyle(() => {
        let transform = [
            {
                rotate: `${derivedRotation.value * 360}deg`,
            },
        ];
        let width = 0;
        let height = 0;
        if (Platform.OS === 'web') {
            width = screenWidth;
            height = Math.min(screenWidth * 9 / 16, screenHeight * 0.9);
        }
        else if (isAppPortrait
            && isDesiredPlayerOrientationPortrait) {
            width = shortVal;
            height = shortVal * 9 / 16;
        }
        else {
            width = longVal;
            height = shortVal;
        }
        let additionalTransforms = [];
        let padding = 0;
        if (isDesiredPlayerOrientationLandscape) {
            const isLeft = desiredPlayerOrientation === OrientationType['LANDSCAPE-LEFT'];
            const multiplier = isLeft ? 1 : -1;
            padding = Math.max(topInset, bottomInset);
            width -= topInset;
            if (isAppPortrait && !isDesiredPlayerOrientationPortrait) {
                const translate = ((width - height) / 2) * multiplier;
                additionalTransforms = [
                    { translateX: translate },
                    { translateY: translate },
                ];
            }
            else if (isAppPortrait && isDesiredPlayerOrientationPortrait) {
                additionalTransforms = [
                    { translateX: 0 },
                    { translateY: 0 },
                ];
            }
        }
        transform.push(...additionalTransforms);
        if (isRotationPaused.value) {
            transform = [];
            width = shortVal;
            height = shortVal * 9 / 16;
        }
        let duration = animationTiming;
        if (position.value < 1) {
            duration = 0;
        }
        const finalHeight = interpolate(position.value, [1, 0], [
            height,
            miniplayerHeight
        ], Extrapolation.CLAMP);
        const finalWidth = interpolate(position.value, [1, 0], [
            width,
            ((miniplayerHeight * 16) / 9)
        ], Extrapolation.CLAMP);
        let paddingLeft = padding;
        let paddingRight = padding;
        const additionalPortraitPadding = padding - topInset;
        if (isFullscreen && desiredPlayerOrientation === OrientationType['LANDSCAPE-LEFT']) {
            paddingLeft = additionalPortraitPadding;
        }
        else if (isFullscreen && desiredPlayerOrientation === OrientationType['LANDSCAPE-RIGHT']) {
            paddingRight = additionalPortraitPadding;
        }
        return {
            transform,
            backgroundColor: 'black',
            paddingLeft: withTiming(isMinimized ? 0 : paddingLeft, { duration }),
            paddingRight: withTiming(isMinimized ? 0 : paddingRight, { duration }),
            width: withTiming(finalWidth, { duration }),
            height: withTiming(finalHeight, { duration }),
            maxHeight: Platform.OS === 'web' ? longVal * 0.9 : undefined,
        };
    }, [
        desiredPlayerOrientation,
        miniplayerHeight,
        appOrientation,
        deviceOrientation,
        isMinimized,
        screenWidth,
        topInset,
        isFullscreen,
    ]);
    const isLightContent = isPlayerEmbedded && !isMinimized;
    useEffect(() => {
        PlaybackEventEmitter.emit(PlaybackEvent.FULLSCREEN, isFullscreen);
        if (isFullscreen) {
            setMode(StatusBarMode.HIDDEN);
        }
        else if (isLightContent) {
            setMode(StatusBarMode.DARK);
        }
        else {
            setMode(StatusBarMode.DEFAULT);
        }
    }, [
        isFullscreen,
        isLightContent,
    ]);
    const miniplayerOpacityStyle = useAnimatedStyle(() => {
        const opacity = interpolate(position.value, [1, 0], [-1, 1], Extrapolation.CLAMP);
        const zIndex = interpolate(position.value, [1, 0], [0, 1], Extrapolation.CLAMP);
        return {
            opacity,
            zIndex,
            alignSelf: 'flex-end',
        };
    }, []);
    return {
        rotationStyle: style,
        miniplayerOpacityStyle,
        desiredPlayerOrientation,
        deviceOrientation,
        isDesiredPlayerOrientationLandscape,
        isDesiredPlayerOrientationPortrait,
        isDevicePortraitOrUpsidedown,
        isDeviceOnlyPortrait: isPortrait(deviceOrientation),
        isDeviceLandscape,
        isAppLandscape,
        isAppPortrait,
        isFullscreen,
        onToggleFullscreen,
        onBack,
        onPauseRotations: () => isRotationPaused.value = true,
        onResumeRotations: () => isRotationPaused.value = false,
    };
};
//# sourceMappingURL=useAnimatedVideoStyle.js.map